import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import {
  ModalBaseStyles,
  ModalActionButtonStyles,
  ModalHeader,
} from "../../Common/styles/modal";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { CohortPreviewResult } from "./types";

interface AddToCohortModalProps {
  open: boolean;
  onClose: () => void;
  selectedParticipants: Set<string>;
  cohortData: CohortPreviewResult | undefined;
  cohortName?: string;
  studyId?: string;
  groupId?: string;
}

const AddToCohortModal: React.FC<AddToCohortModalProps> = ({
  open,
  onClose,
  selectedParticipants,
  cohortData,
  cohortName,
  studyId,
  groupId,
}) => {
  const [submitLoading, setSubmitLoading] = useState(false);

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = async () => {
    try {
      setSubmitLoading(true);
      const response: AxiosResponse = await http.post(
        `/study/${studyId}/participant-groups/${groupId}/enroll`,
        {
          participantIds: Array.from(selectedParticipants),
          cohortQueryId: cohortData?.cohortQueryId,
          cohortQueryRunId: cohortData?.cohortRunId,
        }
      );
      toastMessage("success", response.data.message);
      handleClose();
    } catch (error) {
      errorToastMessage(error as Error);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{ ...ModalBaseStyles, minHeight: "20vh" }}>
        <ModalHeader
          title="Add Participants to User Group"
          onCloseClick={handleClose}
        />

        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ color: "text.primary", mb: 1.5 }}>
            {selectedParticipants.size}{" "}
            {selectedParticipants.size === 1 ? "participant" : "participants"}{" "}
            will be added to{" "}
            <Typography
              component="span"
              sx={{ fontWeight: 600, color: "primary.main" }}
            >
              {cohortName || "this user group"}
            </Typography>
            .
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Are you sure you want to proceed?
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ ...ModalActionButtonStyles, mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={submitLoading}
            data-testid="button-cancel-add-to-cohort"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            Cancel
          </Button>

          <Button
            type="button"
            variant="contained"
            disabled={submitLoading}
            onClick={handleConfirm}
            data-testid="button-confirm-add-to-cohort"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
            }}
          >
            {submitLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Adding...
              </Box>
            ) : (
              "Confirm"
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddToCohortModal;
